import i18n from '../i18n';

/**
 * Utilidad para obtener texto localizado de campos multiidioma
 * Soporta tanto strings simples (compatibilidad hacia atras) como objetos multiidioma
 */

export type LocalizedText = string | { [lang: string]: string };

/** Evita devolver objetos anidados a React (solo hojas string). */
function coerceLocalizedEntry(
  value: unknown,
  fallback: string,
  userLang?: string
): string {
  if (value == null) {
    return fallback;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return getLocalizedText(value as LocalizedText, fallback, userLang);
  }
  return fallback;
}

function resolveUserLang(userLang?: string): string {
  if (userLang) {
    return userLang;
  }
  const activeI18nLanguage = i18n.resolvedLanguage || i18n.language;
  if (activeI18nLanguage) {
    return activeI18nLanguage;
  }
  if (typeof localStorage !== 'undefined') {
    const fromI18n = localStorage.getItem('i18nextLng');
    if (fromI18n) {
      return fromI18n;
    }
    const legacy = localStorage.getItem('language');
    if (legacy) {
      return legacy;
    }
  }
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language.split('-')[0] || 'es';
  }
  return 'es';
}

/**
 * Obtiene el texto localizado segun el idioma del usuario
 * @param text - Texto que puede ser string simple o objeto multiidioma
 * @param fallback - Texto de respaldo si no se encuentra la traduccion
 * @param userLang - Idioma del usuario (por defecto del i18n activo, localStorage o 'es')
 * @returns El texto en el idioma solicitado o el fallback
 */
export function getLocalizedText(
  text: LocalizedText | undefined | null,
  fallback: string = '',
  userLang?: string
): string {
  if (!text) {
    return fallback;
  }

  // Si es un string simple, devolverlo directamente (compatibilidad hacia atras)
  if (typeof text === 'string') {
    return text;
  }

  // Objeto multiidioma: nunca devolver un objeto a React; resolver hojas string o anidar
  if (typeof text === 'object' && !Array.isArray(text)) {
    const lang = resolveUserLang(userLang);
    const baseLang = lang.split('-')[0];
    const dict = text as Record<string, unknown>;

    const tryKeys = [lang, baseLang, 'es', 'en', ...Object.keys(dict)];
    const seen = new Set<string>();

    for (const k of tryKeys) {
      if (!k || seen.has(k) || !Object.prototype.hasOwnProperty.call(dict, k)) {
        continue;
      }
      seen.add(k);
      const raw = dict[k];
      if (raw === undefined || raw === null) {
        continue;
      }
      // String explicito (incluido "")
      if (typeof raw === 'string') {
        return raw;
      }
      const coerced = coerceLocalizedEntry(raw, '', userLang);
      if (coerced !== '') {
        return coerced;
      }
    }
  }

  return fallback;
}

/**
 * Convierte un string simple a formato multiidioma
 * @param text - Texto simple
 * @param lang - Idioma inicial (por defecto 'es')
 * @returns Objeto multiidioma con el texto en el idioma especificado
 */
export function toLocalizedText(text: string, lang: string = 'es'): { [lang: string]: string } {
  return { [lang]: text };
}

/**
 * Verifica si un texto es multiidioma
 * @param text - Texto a verificar
 * @returns true si es un objeto multiidioma, false si es un string simple
 */
export function isLocalizedText(text: LocalizedText | undefined | null): boolean {
  return typeof text === 'object' && text !== null;
}

/**
 * Nombre unico (UniqueName / slug) para mostrar en UI: primera letra mayuscula, resto minusculas.
 * Ej.: `acmecorp` -> `Acmecorp`, `MiCliente` -> `Micliente`
 */
export function formatUniqueNameForDisplay(uniqueName: string | undefined | null): string {
  if (uniqueName == null || typeof uniqueName !== 'string') {
    return '';
  }
  const t = uniqueName.trim();
  if (!t) {
    return '';
  }
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}
